import moment from 'moment';
import type { unitOfTime } from 'moment';
import type { OperateOption } from '@bluevision/tools';
import { isArray, isBoolean, arr2Str, formatStr, goQuery } from '@bluevision/tools';
import { Message } from '@bluevision/components';
import { PROD_HOSTNAME } from '@/data/constant';
import i18n from '@/i18n';
import { IEnterpriseCertification } from '@/services/enterpriseCertification/interface';
import { parse } from 'qs';
import {
  handleFailCertify,
  handleLoginNotCertify,
  handleSuccessCertify,
} from '@/components/RealnameIdentification/data';
import type { CurrentUserInfo } from '@/typings/user';

export const getDateRangeDates = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  range?: any[],
  type: unitOfTime.Diff = 'days',
  format: string = 'YYYY-MM-DD',
) => {
  if (!range?.length) {
    return [];
  }
  let [start, end] = range;
  // 兼容safari浏览器，format不能去掉
  start = moment(start, format);
  end = moment(end, format);
  const days = end.diff(start, type);
  const dates: string[] = [];
  for (let i = 0; i <= days; i += 1) {
    const tmp = moment(start.startOf(type)).add(i, type).format(format);
    dates.push(tmp);
  }
  return dates;
};

// 本日/本周/本月/本季/本年比较简单
// scope分别为/day/week/month/quarter/year
// const startDate = moment().startOf(scope).valueOf() // .valueOf()获取到的是Long，不然就是一个Moment对象
// const endDate = moment().valueOf() // 获取的是当前系统时间
// 自然周
export const currentMonday = moment().startOf('week');
export const currentSunday = moment().endOf('week');
// 上一周
export const lastMonday = moment()
  .week(moment().week() - 1)
  .startOf('week');
export const lastSunday = moment()
  .week(moment().week() - 1)
  .endOf('week');
// 自然月
export const currentMonthStart = moment().startOf('month');
export const currentMonthEnd = moment().endOf('month');
// 上一月
export const lastMonthStart = moment()
  .month(moment().month() - 1)
  .startOf('month');
export const lastMonthEnd = moment()
  .month(moment().month() - 1)
  .endOf('month');
// 上个季度第一个月一号的00:00:00~上个季度最后一个月最后一天的23:59:59
// const startDate = moment().quarter(moment().quarter() - 1).startOf('quarter')
// const endDate = moment().quarter(moment().quarter() - 1).endOf('quarter')
// 自然年
export const currentYearStart = moment().startOf('year');
export const currentYearEnd = moment().endOf('year');
// 上一年
export const lastYearStart = moment()
  .year(moment().year() - 1)
  .startOf('year');
export const lastYearEnd = moment()
  .year(moment().year() - 1)
  .endOf('year');
// 这里有一个需要注意的地方
// 为何每次都是声明一个常量/变量，是因为如果按以下代码写,你会发现你的startDate也变成了当天的结束时间。
// let startDate = moment().startOf('day')
// let endDate = startDate().endOf('day')

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getExcelData = (options: OperateOption[] = [], list: any[] = []) => {
  return [
    options.map((item) => item.title),
    ...list.map((row) =>
      options.map((item) => {
        const { key, format, isEnum, isMultiple, isTime, isBool } = item;
        const value = row?.[key];
        if (isEnum) {
          if (isArray(value)) {
            return arr2Str(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value.map((i: any) => isEnum[i]),
              '、',
            );
          }
          return isEnum[value];
        }
        if (isMultiple) {
          return arr2Str(value, isBoolean(isMultiple) ? undefined : (isMultiple as string));
        }
        if (isTime) {
          return value
            ? moment(value).format(isBoolean(isTime) ? formatStr : (isTime as string))
            : undefined;
        }
        if (isBool) {
          return Boolean[value];
        }
        return format ? format(value, row) : value;
      }),
    ),
  ];
};

/** message.success 封装 */
export const successMessage = (content: React.ReactNode, duration = 3) => {
  Message.success({
    content,
    duration,
  });
};

/** message.error 封装 */
export const errorMessage = (content: React.ReactNode, duration = 3) => {
  Message.error({
    content,
    duration,
  });
};

// 数字以千分位显示
export const formatNumber = (num?: string | number) => {
  if (num === undefined || num === null) {
    return undefined;
  }

  if (typeof num === 'string' && isNaN(Number(num))) {
    return num;
  }

  if (typeof num === 'string' && !isNaN(Number(num))) {
    // 格式类似"12312.00"的字符串，以千分位显示，并且保证精确到小数点后两位
    return Number(num)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 数字以千分位显示，并且保证精确到小数点后两位
  return Number(num)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// 不需要登录的页面
export const noLoginRequired = () => window.location.pathname.startsWith('/user');

/** 去除字符串两边的空格 */
export const trimSpace = (params: { target: { value: string } } | string) => {
  // 如果 params 为一个对象，这个对象是一个事件对象
  if (typeof params === 'object' && 'target' in params) {
    const { value = '' } = params.target;

    return value.trim();
  }

  // 否则 params 是一个字符串
  return (params ?? '').trim();
};

// getErrorMsg返回的错误信息，如果以"Error: "开头，则去掉
export const removeErrorPrefix = (inputString: string): string => {
  if (inputString?.startsWith('Error: ')) {
    return inputString?.substring('Error: '.length);
  }
  return inputString; // 如果字符串不以"Error: "开头，则返回原始字符串
};

// object to Array
export const transformObjectToArray = (obj: Record<string, string>) =>
  Object.entries(obj).map(([key, value]) => ({
    label: i18n.t(value),
    value: key,
  }));

/** 判断是否为线上域名 */
export const isProdDomain = () => {
  return window?.location?.hostname === PROD_HOSTNAME;
};

/** 项目启动后 处理企业认证相关弹窗逻辑 */
export const handleCertification = async (currentUser?: CurrentUserInfo) => {
  // 灰度内用户
  if (currentUser?.signCertificationRequired) {
    // 从重定向的 url 中拿到 query string
    const queryParams: {
      code?: string;
      reason?: string;
      original_certification_type?: IEnterpriseCertification.Common.CertificationType;
    } = parse(location.search, { ignoreQueryPrefix: true });
    // code: 0 成功，1 失败
    const { code, reason, original_certification_type } = queryParams;
    if (code === IEnterpriseCertification.Common.CertificationCode.SUCCESS) {
      // 成功
      handleSuccessCertify();
      goQuery({});
    } else if (code === IEnterpriseCertification.Common.CertificationCode.FAIL) {
      // 失败
      handleFailCertify({
        certificationType: original_certification_type ?? currentUser?.defaultCertificationType,
        failReason: reason,
      });
      goQuery({});
    } else if (
      currentUser?.signCertificationStatus ===
      IEnterpriseCertification.Common.EnterpriseCertificationStatus.UNCERTIFIED
    ) {
      // 需要企业认证 但 未认证，弹窗
      handleLoginNotCertify({ certificationType: currentUser?.defaultCertificationType });
    }
  }
};
